import type { MarketSeriesData } from '../../api/marketPricesClient';
import {
    ownPanelId,
    type ChartSeries,
    type SeriesMoves,
} from '../../event/chart/seriesTypes';
import { leadingOutcome } from '../events/leadingOutcome';
import { eventKey } from '../model/fromAssetDetail';
import type { CandidateEvent } from '../model/types';

export function eventSeriesId(event: CandidateEvent): string {
    return `event:${eventKey(event)}`;
}

export function eventSeries(
    events: readonly CandidateEvent[],
    data: readonly MarketSeriesData[],
    colors: ReadonlyMap<string, string>
): ChartSeries[] {
    const byMarket = new Map(data.map(d => [d.conditionId, d.data]));
    const result: ChartSeries[] = [];
    for (const event of events) {
        const lead = leadingOutcome(event);
        if (!lead) continue;
        const candles = byMarket.get(lead.market.marketId) ?? [];
        result.push({
            id: eventSeriesId(event),
            label:
                event.markets.length > 1
                    ? `${event.title}: ${lead.label}`
                    : event.title,
            color: colors.get(eventKey(event)) ?? '#888888',
            unit: 'probability',
            draw: 'line',
            axis: 'shared',
            home: 'probability',
            movable: true,
            hasData: candles.length > 0,
            image: event.image,
            data: {
                kind: 'points',
                points: candles.map(c => ({ t: c.t.getTime(), v: c.close })),
            },
        });
    }
    return result;
}

export function eventMoves(
    series: readonly ChartSeries[],
    onMain: ReadonlySet<string>
): SeriesMoves {
    const moves: Record<string, ReturnType<typeof ownPanelId>> = {};
    for (const s of series) {
        if (s.id.startsWith('event:') && !onMain.has(s.id)) {
            moves[s.id] = ownPanelId(s.id);
        }
    }
    return moves;
}

export function onMainFromMoves(
    series: readonly ChartSeries[],
    moves: SeriesMoves
): Set<string> {
    const onMain = new Set<string>();
    for (const s of series) {
        if (!s.id.startsWith('event:')) continue;
        const target = moves[s.id];
        if (target === undefined || target === 'probability') onMain.add(s.id);
    }
    return onMain;
}
